import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  getServerInfo,
  getServerExtensions,
  ServerInfoResponse,
  getServerSettings,
  ServerSetting,
  getExternalServerSettings,
  ExternalServerSettings
} from '../../services/endpoints'
import { DefaultState } from '../../types'

const sliceName = 'server'

export type ServerState = {
  version: string
  cpuinfo: {
    modelName: string
    processors: number
  }
  meminfo: {
    memTotal: number
    memFree: number
    memAvail: number
  }
  network: {
    name: string
    mac: string
  }[]
  extensions: ServerExtension[]
  settings: ServerSetting[]
  externalSettings: ExternalServerSettings | null
  loading?: boolean
  error?: string
}

export type ServerExtension = {
  id: number
  val: string
  what: string
  category: string
  version: number
  enabled: boolean
}

export const serverInfo = createAsyncThunk(
  `${sliceName}/serverInfo`,
  async (_, { rejectWithValue }) => {
    try {
      return await getServerInfo()
    } catch (err: any) {
      return rejectWithValue(err)
    }
  }
)

export const fetchServerExtensions = createAsyncThunk(
  `${sliceName}/fetchServerExtensions`,
  async(_, { rejectWithValue }) => {
    try {
      return await getServerExtensions()
    } catch (err: any) {
      return rejectWithValue(err)
    }
  }
)

export const fetchServerSettings = createAsyncThunk(
  `${sliceName}/fetchServerSettings`,
  async (_, { rejectWithValue }) => {
    try {
      return await getServerSettings()
    } catch (err: any) {
      return rejectWithValue(err)
    }
  }
)

export const fetchExternalServerSettings = createAsyncThunk(
  `${sliceName}/fetchExternalServerSettings`,
  async (_, { rejectWithValue }) => {
    try {
      return await getExternalServerSettings()
    } catch (err: any) {
      return rejectWithValue(err)
    }
  }
)

export const initialServerState: ServerState = {
  version: '',
  cpuinfo: {
    modelName: '',
    processors: 0
  },
  meminfo: {
    memTotal: 0,
    memFree: 0,
    memAvail: 0
  },
  network: [
    {
      name: '',
      mac: ''
    }
  ],
  extensions: [],
  settings: [],
  externalSettings: null
}

export const selectService29Enabled = (state: DefaultState): boolean =>
  state.server.externalSettings?.features?.service29 === true

export const serverSlice = createSlice({
  name: sliceName,
  initialState: initialServerState,
  reducers: {
    updateServer: (state, action: PayloadAction<ServerInfoResponse>) => {
      state.version = action.payload.version
      state.cpuinfo = action.payload.cpuinfo
      state.meminfo = action.payload.meminfo
      state.network = action.payload.network
      state.extensions = action.payload.extensions
      state.loading = false
      return state
    }
  },
  extraReducers(builder) {
    builder
      .addCase(
        serverInfo.fulfilled,
        (state, action: PayloadAction<ServerInfoResponse>) => {
          state.version = action.payload.version
          state.cpuinfo = action.payload.cpuinfo
          state.meminfo = action.payload.meminfo
          state.network = action.payload.network
          state.extensions = action.payload.extensions
          state.loading = false
        }
      )
      .addCase(serverInfo.pending, (state) => {
        state = {
          version: '',
          cpuinfo: {
            modelName: '',
            processors: 0
          },
          meminfo: {
            memTotal: 0,
            memFree: 0,
            memAvail: 0
          },
          network: [
            {
              name: '',
              mac: ''
            }
          ],
          settings: state.settings,
          extensions: [],
          externalSettings: state.externalSettings
        }
        state.loading = true
      })
      .addCase(serverInfo.rejected, (state, action) => {
        state = {
          version: '',
          cpuinfo: {
            modelName: '',
            processors: 0
          },
          meminfo: {
            memTotal: 0,
            memFree: 0,
            memAvail: 0
          },
          network: [
            {
              name: '',
              mac: ''
            }
          ],
          settings: state.settings,
          extensions: [],
          externalSettings: state.externalSettings
        }
        state.loading = false
        state.error = action.error.message
      })
      .addCase(
        fetchServerExtensions.fulfilled,
        (state, action: PayloadAction<ServerExtension[]>) => {
          state.extensions = action.payload
        }
      )
      .addCase(
        fetchServerExtensions.rejected,
        (state, action: PayloadAction<ServerExtension[]>) => {
          state.error = action.error.message
        }
      )
      .addCase(
        fetchServerSettings.fulfilled,
        (state, action: PayloadAction<ServerSetting[]>) => {
          state.settings = action.payload
        }
      )
      .addCase(fetchServerSettings.rejected, (state, action) => {
        state.settings = []
        state.error = action.error.message
      })
      .addCase(
        fetchExternalServerSettings.fulfilled,
        (state, action: PayloadAction<ExternalServerSettings>) => {
          state.externalSettings = action.payload
        }
      )
      .addCase(fetchExternalServerSettings.rejected, (state) => {
        state.externalSettings = null
      })
  }
})
